import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { OtpInput } from "components/ui/otp-input";
import { useAppDispatch } from "../../../redux/useDispatch";
import {
  useConfirmEmailChangeMutation,
  useRequestEmailChangeMutation,
} from "api/account/accountApiSlice";
import { AccountApiError, AccountProfile } from "api/account/types";
import { setAccountProfile } from "../../../redux/slice/account/accountSlice";
import {
  cleanEmail,
  getEmailSuggestion,
  isEmailAcceptable,
} from "util/validation";

interface EmailChangeSectionProps {
  profile: AccountProfile;
}

/** OTP-failure codes on confirm that mean "the code is no longer usable". */
const EXPIRED_ERROR_CODES = ["expired", "too_many_attempts"];

/**
 * H-3a F2 — email change with OTP verification. The code is mailed to the NEW
 * address (proves ownership of it). Flow state is local: idle → code sent →
 * confirmed (profile updated via Redux, flow resets to idle).
 */
const EmailChangeSection = ({ profile }: EmailChangeSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [requestEmailChange, { isLoading: isSendingCode }] =
    useRequestEmailChangeMutation();
  const [confirmEmailChange, { isLoading: isConfirming }] =
    useConfirmEmailChangeMutation();

  // Opened by the explicit button rather than by editing a field: a
  // verification flow that starts on a keystroke would fire codes at a
  // mistyped character.
  const [isOpen, setIsOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [changeSuccess, setChangeSuccess] = useState(false);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewEmail(event.target.value);
    setEmailError(null);
    setSuggestion(null);
    setChangeSuccess(false);
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    setNewEmail(suggestion);
    setEmailError(null);
    setSuggestion(null);
  };

  const handleSendCode = async () => {
    const cleanedEmail = cleanEmail(newEmail);
    if (!cleanedEmail) {
      setEmailError(t("account.login.error_required"));
      return;
    }

    const suggested = getEmailSuggestion(cleanedEmail);
    if (suggested) {
      setSuggestion(suggested);
      setEmailError(t("account.login.error_typo_suggestion"));
      return;
    }

    if (!isEmailAcceptable(cleanedEmail)) {
      setEmailError(t("account.login.error_invalid"));
      return;
    }

    try {
      await requestEmailChange({ newEmail: cleanedEmail }).unwrap();
      setCodeSent(true);
      setCode("");
      setCodeError(null);
    } catch {
      setEmailError(t("account.login.error_send_failed"));
    }
  };

  const handleCodeChange = (nextValue: string) => {
    setCode(nextValue);
    setCodeError(null);
  };

  const handleConfirm = async () => {
    if (code.length !== 6) {
      setCodeError(t("account.verify.error_invalid_length"));
      return;
    }

    try {
      const updated = await confirmEmailChange({ code }).unwrap();
      dispatch(setAccountProfile(updated));
      setCodeSent(false);
      setCode("");
      setNewEmail("");
      setChangeSuccess(true);
    } catch (error) {
      const apiError = error as AccountApiError;
      if (apiError?.status === 409) {
        // Distinct message: the address belongs to another account.
        setCodeError(t("account.email_change.error_email_taken"));
        return;
      }
      const errorCode = apiError?.data?.code?.toLowerCase();
      setCodeError(
        errorCode !== undefined && EXPIRED_ERROR_CODES.includes(errorCode)
          ? t("account.verify.error_expired")
          : t("account.verify.error_wrong_code")
      );
    }
  };

  // Collapsed until asked for. Changing an address is a verification ceremony —
  // request a code, read the mailbox, enter the code — and a form that shows all
  // of that permanently reads as something the person is expected to do. The
  // current address lives with the rest of the profile above; this is only the
  // machinery for replacing it (PO, 2026-08-20).
  if (!isOpen) {
    return (
      <Button
        size="lg"
        variant="outline"
        className="w-full"
        onClick={() => setIsOpen(true)}
      >
        {t("account.email_change.open")}
      </Button>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-medium text-slate-700">
        {t("account.email_change.title")}
      </h2>

      <div className="space-y-2">
        <label
          htmlFor="account-new-email"
          className="text-sm font-medium text-slate-700"
        >
          {t("account.email_change.new_email_label")}
        </label>
        <Input
          id="account-new-email"
          type="email"
          autoComplete="email"
          placeholder={t("account.login.email_placeholder")}
          value={newEmail}
          onChange={handleEmailChange}
          error={Boolean(emailError)}
          disabled={isSendingCode || isConfirming}
          className="w-full"
        />
        {emailError && <p className="text-sm text-red-600">{emailError}</p>}
        {suggestion && (
          <button
            type="button"
            onClick={applySuggestion}
            className="inline-flex items-center rounded-full border border-primary px-3 py-1 text-sm text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {suggestion}
          </button>
        )}
      </div>

      {changeSuccess && (
        <p className="text-sm text-slate-600" aria-live="polite">
          {t("account.email_change.success")}
        </p>
      )}

      <Button
        size="lg"
        variant="outline"
        className="w-full"
        disabled={isSendingCode || isConfirming || !newEmail.trim()}
        onClick={handleSendCode}
      >
        {isSendingCode ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t("account.login.sending")}
          </>
        ) : (
          t("account.email_change.send_code")
        )}
      </Button>

      {codeSent && (
        <div className="space-y-2">
          <p className="text-center text-sm text-slate-600">
            {t("account.email_change.code_sent")}
          </p>
          <OtpInput
            value={code}
            onChange={handleCodeChange}
            length={6}
            disabled={isConfirming}
            error={Boolean(codeError)}
            autoFocus
            className="justify-center"
          />
          {codeError && (
            <p className="text-center text-sm text-red-600">{codeError}</p>
          )}
          <Button
            size="lg"
            variant="primary"
            className="w-full"
            disabled={isConfirming || code.length !== 6}
            onClick={handleConfirm}
          >
            {isConfirming ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {t("account.verify.verifying")}
              </>
            ) : (
              t("account.email_change.confirm")
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmailChangeSection;
